import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const PropertyTable = ({ properties, onPropertySelect, selectedProperty, onBulkSelect, selectedRows }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'propertyId', direction: 'asc' });
  const [editingCell, setEditingCell] = useState(null);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedProperties = [...properties]?.sort((a, b) => {
    if (sortConfig?.key) {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      
      if (typeof aValue === 'string') {
        return sortConfig?.direction === 'asc' 
          ? aValue?.localeCompare(bValue)
          : bValue?.localeCompare(aValue);
      }
      
      return sortConfig?.direction === 'asc' 
        ? aValue - bValue
        : bValue - aValue;
    }
    return 0;
  });

  const getStatusColor = (status) => {
    const colors = {
      'Occupied': 'var(--color-success)',
      'Vacant': 'var(--color-warning)',
      'Under Maintenance': 'var(--color-accent)',
      'Notice Period': 'var(--color-error)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const handleCellEdit = (propertyId, field, value) => {
    console.log('Editing:', propertyId, field, value);
    setEditingCell(null);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onBulkSelect(sortedProperties?.map(p => p?.propertyId));
    } else {
      onBulkSelect([]);
    }
  };

  const handleRowSelect = (propertyId, checked) => {
    if (checked) {
      onBulkSelect([...selectedRows, propertyId]);
    } else {
      onBulkSelect(selectedRows?.filter(id => id !== propertyId));
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} color="var(--color-muted-foreground)" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={14} color="var(--color-accent)" />
      : <Icon name="ChevronDown" size={14} color="var(--color-accent)" />;
  };

  return (
    <div className="h-full flex flex-col bg-surface rounded-lg border border-border">
      <div className="overflow-x-auto flex-1">
        <table className="w-full">
          <thead className="sticky top-0 bg-surface border-b border-border z-10">
            <tr>
              <th className="px-4 py-3 text-left">
                <Checkbox
                  checked={selectedRows?.length === sortedProperties?.length && sortedProperties?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  size="sm"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('propertyId')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-smooth font-caption"
                >
                  Property ID
                  <SortIcon columnKey="propertyId" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('address')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-smooth font-caption"
                >
                  Address
                  <SortIcon columnKey="address" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('landlord')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-smooth font-caption"
                >
                  Landlord
                  <SortIcon columnKey="landlord" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('tenant')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-smooth font-caption"
                >
                  Tenant
                  <SortIcon columnKey="tenant" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('tenancyStart')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-smooth font-caption"
                >
                  Tenancy Start
                  <SortIcon columnKey="tenancyStart" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('tenancyEnd')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-smooth font-caption"
                >
                  Tenancy End
                  <SortIcon columnKey="tenancyEnd" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('monthlyRent')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-smooth font-caption"
                >
                  Monthly Rent
                  <SortIcon columnKey="monthlyRent" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-smooth font-caption"
                >
                  Status
                  <SortIcon columnKey="status" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedProperties?.map((property, index) => (
              <tr
                key={property?.propertyId}
                onClick={() => onPropertySelect(property)}
                className={`table-row cursor-pointer transition-smooth ${
                  selectedProperty?.propertyId === property?.propertyId
                    ? 'bg-accent/10' :'hover:bg-muted'
                }`}
              >
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedRows?.includes(property?.propertyId)}
                    onChange={(e) => handleRowSelect(property?.propertyId, e?.target?.checked)}
                    size="sm"
                  />
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-accent font-data">{property?.propertyId}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="max-w-xs">
                    <p className="text-sm text-foreground truncate">{property?.address}</p>
                    <p className="text-xs text-muted-foreground">{property?.postcode}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{property?.landlord}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{property?.tenant || '-'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground font-data">{property?.tenancyStart || '-'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground font-data">{property?.tenancyEnd || '-'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground font-data">£{property?.monthlyRent?.toLocaleString()}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: getStatusColor(property?.status) }}
                    />
                    <span className="text-sm text-foreground font-caption">{property?.status}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t border-border flex items-center justify-between bg-surface">
        <p className="text-sm text-muted-foreground font-caption">
          Showing {sortedProperties?.length} properties
          {selectedRows?.length > 0 && ` • ${selectedRows?.length} selected`}
        </p>
        <div className="flex items-center gap-2">
          <button className="h-8 w-8 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted disabled:opacity-50">
            <Icon name="ChevronLeft" size={16} color="var(--color-muted-foreground)" />
          </button>
          <span className="text-sm text-foreground font-data">1 / 1</span>
          <button className="h-8 w-8 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted disabled:opacity-50">
            <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyTable;